import { useEffect, useRef, useState } from 'react'
import type { Socket } from 'socket.io-client'
import type { UserProfile } from '../../shared/types'

interface PeerState {
  connection: RTCPeerConnection
  pendingCandidates: RTCIceCandidateInit[]
}

interface IncomingSignal {
  from: string
  data: {
    type?: RTCSdpType
    sdp?: string
    candidate?: RTCIceCandidateInit
  }
}

export function useMeshVideo(
  enabled: boolean,
  socket: Socket | null,
  localStream: MediaStream | null,
  users: UserProfile[],
) {
  const peersRef = useRef(new Map<string, PeerState>())
  const localStreamRef = useRef(localStream)
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(
    new Map(),
  )

  useEffect(() => {
    localStreamRef.current = localStream
  }, [localStream])

  useEffect(() => {
    if (!enabled || !socket) {
      peersRef.current.forEach((peer) => peer.connection.close())
      peersRef.current.clear()
      setRemoteStreams(new Map())
      return
    }
    const peers = peersRef.current

    const createPeer = (remoteSocketId: string): PeerState => {
      const existing = peers.get(remoteSocketId)
      if (existing) return existing

      const connection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.cloudflare.com:3478' }],
        bundlePolicy: 'max-bundle',
      })
      const state: PeerState = { connection, pendingCandidates: [] }
      peers.set(remoteSocketId, state)

      localStreamRef.current?.getVideoTracks().forEach((track) => {
        const sender = connection.addTrack(track, localStreamRef.current!)
        const parameters = sender.getParameters()
        parameters.encodings ??= [{}]
        parameters.encodings[0].maxBitrate = 180_000
        parameters.encodings[0].maxFramerate = 10
        void sender.setParameters(parameters).catch(() => undefined)
      })

      connection.onicecandidate = (event) => {
        if (!event.candidate) return
        socket.emit('video:signal', {
          target: remoteSocketId,
          data: { candidate: event.candidate.toJSON() },
        })
      }
      connection.ontrack = (event) => {
        const stream = event.streams[0] || new MediaStream([event.track])
        setRemoteStreams((current) => {
          const next = new Map(current)
          next.set(remoteSocketId, stream)
          return next
        })
      }
      connection.onconnectionstatechange = () => {
        if (['failed', 'closed'].includes(connection.connectionState)) {
          connection.close()
          peers.delete(remoteSocketId)
          setRemoteStreams((current) => {
            const next = new Map(current)
            next.delete(remoteSocketId)
            return next
          })
        }
      }
      return state
    }

    const flushCandidates = async (state: PeerState) => {
      if (!state.connection.remoteDescription) return
      for (const candidate of state.pendingCandidates.splice(0)) {
        await state.connection.addIceCandidate(candidate).catch(() => undefined)
      }
    }

    const initiate = async (remoteSocketId: string) => {
      const state = createPeer(remoteSocketId)
      if (state.connection.signalingState !== 'stable') return
      const offer = await state.connection.createOffer()
      await state.connection.setLocalDescription(offer)
      socket.emit('video:signal', {
        target: remoteSocketId,
        data: { type: offer.type, sdp: offer.sdp },
      })
    }

    const onSignal = async ({ from, data }: IncomingSignal) => {
      const state = createPeer(from)
      const connection = state.connection
      if (data.candidate) {
        if (connection.remoteDescription) {
          await connection.addIceCandidate(data.candidate).catch(() => undefined)
        } else {
          state.pendingCandidates.push(data.candidate)
        }
        return
      }
      if (!data.type || !data.sdp) return

      if (data.type === 'offer') {
        await connection.setRemoteDescription({ type: 'offer', sdp: data.sdp })
        await flushCandidates(state)
        const answer = await connection.createAnswer()
        await connection.setLocalDescription(answer)
        socket.emit('video:signal', {
          target: from,
          data: { type: answer.type, sdp: answer.sdp },
        })
      } else if (data.type === 'answer') {
        await connection.setRemoteDescription({ type: 'answer', sdp: data.sdp })
        await flushCandidates(state)
      }
    }

    socket.on('video:signal', onSignal)

    const remoteIds = users
      .map((user) => user.socketId)
      .filter((id): id is string => Boolean(id && id !== socket.id))
      .sort()
      .slice(0, 15)

    for (const remoteId of remoteIds) {
      createPeer(remoteId)
      if (socket.id && socket.id.localeCompare(remoteId) < 0) {
        void initiate(remoteId)
      }
    }

    for (const [remoteId, state] of peers) {
      if (remoteIds.includes(remoteId)) continue
      state.connection.close()
      peers.delete(remoteId)
      setRemoteStreams((current) => {
        const next = new Map(current)
        next.delete(remoteId)
        return next
      })
    }

    return () => {
      socket.off('video:signal', onSignal)
    }
  }, [enabled, socket, users, localStream])

  useEffect(() => {
    return () => {
      peersRef.current.forEach((peer) => peer.connection.close())
      peersRef.current.clear()
    }
  }, [])

  return remoteStreams
}
